import { useCallback, useEffect, useState, useMemo, memo } from "react"
import { DateTime, Duration } from "luxon"
import MonthYearCalendar, {
  Period,
  MonthValue,
  WithCalendarHeader,
  DisallowedMonths,
} from "./MonthYearCalendar"

interface Props {
  value?: string | null
  onChange: (value: string | undefined) => void
  minValue?: string
  maxValue?: string
  disallowedMonths?: DisallowedMonths
  withReset?: true
  fullWidth?: true
}

const validMonthValues: Record<string, boolean> = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].reduce(
  (collection, item) => ({
    ...collection,
    [item.toString().padStart(2, "0")]: true,
  }),
  {},
)
function isMonthValue(value: string): value is MonthValue {
  return validMonthValues[value]
}

const isoRegex = /^\d{4}-\d{2}$/
function isPeriod(value?: string | null): value is Period {
  if (!value) {
    return false
  }

  if (!isoRegex.test(value)) {
    return false
  }

  const [, month] = value.split("-")
  return isMonthValue(month)
}

function SimpleMonthYearCalendar({
  value,
  onChange,
  minValue,
  maxValue,
  disallowedMonths,
  withReset,
  fullWidth,
}: Props) {
  const [yearValue, setYearValue] = useState<string | undefined>("")
  const [monthValue, setMonthValue] = useState<MonthValue>("" as MonthValue)

  const selectMonthAndYear = () => {
    if (isPeriod(value) || !withReset) {
      const valueToParse = value || new Date().toISOString()
      return valueToParse.split("-")
    } else {
      return [undefined, undefined]
    }
  }

  useEffect(() => {
    const [year, month] = selectMonthAndYear()
    setYearValue(year)
    setMonthValue(month as MonthValue)
  }, [value])

  useEffect(() => {
    const newValue = `${yearValue}-${monthValue}`
    if (isPeriod(newValue) && newValue !== value) {
      onChange(newValue)
    }
  }, [yearValue, monthValue])

  const onReset = () => {
    onChange(undefined)
  }

  const onMonthAndYearChange = useCallback<WithCalendarHeader["onMonthAndYearChange"]>(
    (month, year) => {
      setMonthValue(month)
      setYearValue(year.toString())
    },
    [setMonthValue, setYearValue],
  )

  const onYearChange = useCallback(
    (year: number | undefined) => setYearValue(year?.toString()),
    [setYearValue],
  )

  const lockPeriodAfterValue = useMemo(
    () => (isPeriod(maxValue) ? maxValue : undefined),
    [maxValue],
  )

  const lockPeriodBeforeValue = useMemo(() => {
    if (!isPeriod(minValue)) {
      return undefined
    }

    return DateTime.fromISO(minValue)
      .minus(Duration.fromObject({ months: 1 }))
      .toFormat("yyyy-MM") as Period
  }, [minValue])

  return (
    <MonthYearCalendar
      monthValue={monthValue}
      yearValue={yearValue}
      onMonthAndYearChange={onMonthAndYearChange}
      onMonthChange={setMonthValue}
      onYearChange={onYearChange}
      lockPeriodAfter={lockPeriodAfterValue}
      lockPeriodBefore={lockPeriodBeforeValue}
      disallowedMonths={disallowedMonths}
      withHeader
      onReset={onReset}
      withReset={withReset}
      fullWidth={fullWidth}
    />
  )
}

export default memo(SimpleMonthYearCalendar)
